import React, { useState, useCallback, memo } from "react";
import { motion, AnimatePresence } from "framer-motion";

const FormField = memo(
  ({
    label,
    name,
    type = "text",
    placeholder,
    required = false,
    component = "input",
    options = [],
    value,
    error,
    focusedField,
    onFocus,
    onBlur,
    onChange,
  }) => {
    const isFocused = focusedField === name;
    const hasError = error;
    const hasValue = value;

    return (
      <motion.div variants={itemVariants} className="relative">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>

        <div className="relative">
          {component === "select" ? (
            <select
              name={name}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              className={`
              w-full px-4 py-3 rounded-xl border transition-all duration-300
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              ${
                hasError
                  ? "border-red-500 ring-2 ring-red-200"
                  : isFocused
                  ? "border-primary-500"
                  : "border-gray-300 dark:border-gray-600"
              }
              ${hasValue ? "bg-primary-50 dark:bg-primary-900/20" : ""}
            `}
            >
              <option value="">{placeholder}</option>
              {options.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          ) : component === "textarea" ? (
            <textarea
              name={name}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              rows="5"
              className={`
              w-full px-4 py-3 rounded-xl border transition-all duration-300 resize-none
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              placeholder-gray-500 dark:placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              ${
                hasError
                  ? "border-red-500 ring-2 ring-red-200"
                  : isFocused
                  ? "border-primary-500"
                  : "border-gray-300 dark:border-gray-600"
              }
              ${hasValue ? "bg-primary-50 dark:bg-primary-900/20" : ""}
            `}
            />
          ) : (
            <input
              type={type}
              name={name}
              value={value}
              onChange={onChange}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={placeholder}
              className={`
              w-full px-4 py-3 rounded-xl border transition-all duration-300
              bg-white dark:bg-gray-800 text-gray-900 dark:text-white
              placeholder-gray-500 dark:placeholder-gray-400
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              ${
                hasError
                  ? "border-red-500 ring-2 ring-red-200"
                  : isFocused
                  ? "border-primary-500"
                  : "border-gray-300 dark:border-gray-600"
              }
              ${hasValue ? "bg-primary-50 dark:bg-primary-900/20" : ""}
            `}
            />
          )}

          {/* Animated border - only for non-select elements */}
          {isFocused && component !== "select" && (
            <motion.div
              layoutId="formFocus"
              className="absolute inset-0 border-2 border-primary-500 rounded-xl pointer-events-none"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
            />
          )}
        </div>

        {/* Error Message */}
        <AnimatePresence>
          {hasError && (
            <motion.p
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              {hasError}
            </motion.p>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }
);

const containerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
};

const projectTypes = [
  "Web Application",
  "Mobile App",
  "E-commerce Site",
  "Portfolio Website",
  "Custom Software",
  "API Development",
  "Other",
];

const budgetRanges = [
  "Under $1,000",
  "$1,000 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+",
  "Let's Discuss",
];

const timelineOptions = [
  "ASAP",
  "1-2 weeks",
  "1 month",
  "2-3 months",
  "3+ months",
  "Flexible",
];

const ContactForm = ({ isVisible = true }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
    projectType: "",
    budget: "",
    timeline: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  const [focusedField, setFocusedField] = useState(null);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message should be at least 10 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Call the Netlify function backend
      const response = await fetch('/.netlify/functions/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          message: formData.message,
          projectType: formData.projectType || null
        })
      });

      const data = await response.json();

      if (data.success) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          subject: "",
          message: "",
          projectType: "",
          budget: "",
          timeline: "",
        });
      } else {
        setSubmitStatus("error");
        console.error('Contact form error:', data.message);
      }
    } catch (error) {
      console.error('Contact form submission error:', error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = useCallback(
    (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Clear error when user starts typing
      if (errors[name]) {
        setErrors((prev) => ({
          ...prev,
          [name]: "",
        }));
      }
    },
    [errors]
  );

  const handleFocus = useCallback((e) => {
    setFocusedField(e.target.name);
  }, []);

  const handleBlur = useCallback(() => {
    setFocusedField(null);
  }, []);

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-xl border border-gray-200 dark:border-gray-700"
    >
      {/* Form Header */}
      <motion.div variants={itemVariants} className="mb-8">
        <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          Start Your Project
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Fill out the form below and I'll get back to you within 24 hours.
        </p>
      </motion.div>

      {/* Success/Error Messages */}
      <AnimatePresence>
        {submitStatus && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            className={`
              mb-6 p-4 rounded-xl border
              ${
                submitStatus === "success"
                  ? "bg-green-50 border-green-200 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200"
                  : "bg-red-50 border-red-200 text-red-800 dark:bg-red-900/20 dark:border-red-800 dark:text-red-200"
              }
            `}
          >
            <div className="flex items-center">
              {submitStatus === "success" ? (
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              ) : (
                <svg
                  className="w-5 h-5 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              )}
              <span className="font-medium">
                {submitStatus === "success"
                  ? "Message sent successfully! I'll get back to you soon."
                  : "Something went wrong. Please try again or contact me directly."}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Info */}
        <div className="grid md:grid-cols-2 gap-6">
          <FormField
            label="Full Name"
            name="name"
            placeholder="Your name"
            required
            value={formData.name}
            error={errors.name}
            focusedField={focusedField}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <FormField
            label="Email Address"
            name="email"
            type="email"
            placeholder="your@email.com"
            required
            value={formData.email}
            error={errors.email}
            focusedField={focusedField}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Project Details */}
        <FormField
          label="Subject"
          name="subject"
          placeholder="Brief subject line"
          required
          value={formData.subject}
          error={errors.subject}
          focusedField={focusedField}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        <div className="grid md:grid-cols-3 gap-6">
          <FormField
            label="Project Type"
            name="projectType"
            component="select"
            placeholder="Select project type"
            options={projectTypes}
            value={formData.projectType}
            error={errors.projectType}
            focusedField={focusedField}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <FormField
            label="Budget Range"
            name="budget"
            component="select"
            placeholder="Select budget range"
            options={budgetRanges}
            value={formData.budget}
            error={errors.budget}
            focusedField={focusedField}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          <FormField
            label="Timeline"
            name="timeline"
            component="select"
            placeholder="Select timeline"
            options={timelineOptions}
            value={formData.timeline}
            error={errors.timeline}
            focusedField={focusedField}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
        </div>

        {/* Message */}
        <FormField
          label="Project Description"
          name="message"
          component="textarea"
          placeholder="Tell me about your project, goals, and any specific requirements..."
          required
          value={formData.message}
          error={errors.message}
          focusedField={focusedField}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={isSubmitting}
          className={`
            w-full py-4 px-8 rounded-xl font-semibold text-white
            transition-all duration-300 transform
            ${
              isSubmitting
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-gradient-to-r from-primary-500 to-accent-500 hover:from-primary-600 hover:to-accent-600 hover:shadow-lg hover:-translate-y-1"
            }
          `}
          whileHover={!isSubmitting ? { scale: 1.02 } : {}}
          whileTap={!isSubmitting ? { scale: 0.98 } : {}}
        >
          {isSubmitting ? (
            <div className="flex items-center justify-center">
              <motion.div
                className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              />
              Sending Message...
            </div>
          ) : (
            <div className="flex items-center justify-center">
              <span>Send Message</span>
              <motion.svg
                className="w-5 h-5 ml-2"
                fill="currentColor"
                viewBox="0 0 20 20"
                whileHover={{ x: 5 }}
              >
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </motion.svg>
            </div>
          )}
        </motion.button>

        {/* Form Footer */}
        <motion.div variants={itemVariants} className="text-center pt-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            By submitting this form, you agree to my{" "}
            <a
              href="/privacy"
              className="text-primary-500 hover:text-primary-600 underline"
            >
              privacy policy
            </a>
            . I'll never share your information.
          </p>
        </motion.div>
      </form>
    </motion.div>
  );
};

export default ContactForm;
